<template>
	<div class="transaction-search">
		<div class="search">
			<AppIcon :name="IconName.Search" class="icon" :size="14" :stroke-width="2" />
			<input
				:id="inputId"
				v-model="query"
				type="search"
				inputmode="numeric"
				autocomplete="off"
				:placeholder="t('comparableAnalysis.searchPlaceholder')"
				:disabled="disabled"
				@keydown.enter.prevent="selectFirst"
				@keydown.escape="clear"
			/>
		</div>
		<div v-if="isSearchable" class="results">
			<p v-if="searching" class="status">{{ t("comparableAnalysis.searching") }}</p>
			<div v-else-if="searchFailed" class="status failed">
				<span>{{ t("comparableAnalysis.searchFailed") }}</span>
				<Button type="button" variant="ghost" size="xs" @click="startSearch">
					{{ t("comparableAnalysis.retry") }}
				</Button>
			</div>
			<p v-else-if="!results.length" class="status">{{ t("comparableAnalysis.noTransactionFound") }}</p>
			<button
				v-for="result in results"
				v-else
				:key="result.inscriptionNumber"
				type="button"
				class="result"
				:disabled="!isSelectable(result)"
				@click="select(result)"
			>
				<span class="number">{{ formatInscriptionNumber(result.inscriptionNumber) }}</span>
				<span class="details">{{ details(result) }}</span>
				<span v-if="isSelected(result)" class="state">
					<AppIcon :name="IconName.Check" :size="12" :stroke-width="2" />
					{{ t("comparableAnalysis.added") }}
				</span>
				<span v-else-if="limitReached" class="state">{{ t("comparableAnalysis.limitReached") }}</span>
			</button>
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useDebounceFn } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import type { Transaction } from "@/model/DataModel"
import { normalizeComparableInscriptionNumber, useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
import { normalizeTransactionInscriptionNumber } from "@/types/searchType"
import { formatInscriptionNumber } from "@/utils/format"
import {
	formatComparableTransactionDetails,
	summarizeComparableTransaction,
} from "@/utils/comparableTransactionSummary"

const MIN_INSCRIPTION_DIGITS = 7

const props = defineProps<{ inputId?: string; selected: string[]; limitReached?: boolean; disabled?: boolean }>()
const emit = defineEmits<{ (event: "select", inscriptionNumber: string): void }>()
const { t, locale } = useI18n()
const store = useComparableAnalysisStore()
const query = ref("")
const results = ref<Transaction[]>([])
const searching = ref(false)
const searchFailed = ref(false)
let revision = 0

const digits = computed(() => query.value.replace(/\D+/g, ""))
const isSearchable = computed(() => digits.value.length >= MIN_INSCRIPTION_DIGITS)

const search = useDebounceFn(async (request: number) => {
	try {
		const found = await store.findTransactions(normalizeTransactionInscriptionNumber(query.value.trim()))
		if (request === revision) results.value = found.filter(result => result.inscriptionNumber)
	} catch {
		if (request === revision) searchFailed.value = true
	} finally {
		if (request === revision) searching.value = false
	}
}, 300)

function startSearch() {
	const request = ++revision
	results.value = []
	searchFailed.value = false
	searching.value = isSearchable.value
	if (isSearchable.value) void search(request)
}

watch(query, startSearch)

function numberOf(result: Transaction): string {
	return normalizeComparableInscriptionNumber(result.inscriptionNumber ?? "")
}
function isSelected(result: Transaction): boolean {
	return props.selected.includes(numberOf(result))
}
function isSelectable(result: Transaction): boolean {
	return !props.disabled && !props.limitReached && !isSelected(result) && /^\d+$/.test(numberOf(result))
}
function details(result: Transaction): string {
	const summary = summarizeComparableTransaction(result)
	return [summary.address, ...formatComparableTransactionDetails(summary, locale.value)].filter(Boolean).join(" · ")
}
function clear() {
	query.value = ""
}
function select(result: Transaction) {
	if (!isSelectable(result)) return
	emit("select", numberOf(result))
	clear()
}
function selectFirst() {
	const first = results.value.find(isSelectable)
	if (first) select(first)
}
</script>
<style scoped>
.transaction-search {
	position: relative;
	.search {
		position: relative;
		.icon {
			position: absolute;
			top: 50%;
			left: var(--spacing-sm);
			transform: translateY(-50%);
			color: var(--neutral-600);
			pointer-events: none;
		}
		input {
			width: 100%;
			height: var(--size-button-s);
			box-sizing: border-box;
			padding: 0 var(--spacing-sm) 0 calc(var(--spacing-sm) * 2 + var(--space-14));
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			font: inherit;
			font-size: var(--font-size-small);
			color: var(--neutral-900);
			outline: none;
			&:focus {
				border-color: var(--neutral-900);
			}
		}
	}
	.results {
		position: absolute;
		top: calc(100% + var(--spacing-xs));
		left: 0;
		right: 0;
		z-index: 10;
		display: flex;
		flex-direction: column;
		padding: var(--spacing-xs);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		background: var(--neutral-0);
		box-shadow: var(--shadow-lg);
	}
	.status {
		margin: 0;
		padding: var(--spacing-sm);
		font-size: var(--font-size-small);
		color: var(--neutral-600);
		&.failed {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);
		}
	}
	.result {
		display: grid;
		grid-template-columns: auto 1fr auto;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm);
		border: 0;
		border-radius: var(--border-radius-sm);
		background: transparent;
		font: inherit;
		font-size: var(--font-size-small);
		text-align: left;
		color: var(--neutral-900);
		cursor: pointer;
		&:hover:not(:disabled) {
			background: var(--neutral-100);
		}
		&:disabled {
			cursor: default;
		}
		.number {
			font-weight: var(--font-weight-semibold);
		}
		.details {
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
			color: var(--neutral-600);
		}
		.state {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-xxs);
			font-size: var(--font-size-xs);
			color: var(--primary-green);
		}
	}
}
</style>
